import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import type { FleetPendingPathsStatus } from '../api/client'

/** Return value of useStagedPaths — a Set for O(1) exact-path lookups, plus
 * the raw status (for the rare caller that wants pending_count/SHAs too). */
export interface StagedPaths {
  status: FleetPendingPathsStatus | null
  /** The staged-path set, ready for isPathStaged/isPrefixStaged below. Empty
   * (not just unpopulated) both before the first fetch resolves and when
   * GitHub isn't configured for this fleet — callers never need to guard on
   * `status` before checking a path. */
  paths: Set<string>
  /** Re-fetch. Needed by any caller that itself CREATES a staged path (ADR-132's
   * Resources area): a fetch-once-on-mount set would not contain the file the
   * operator just proposed, so the rail would look like the propose did nothing. */
  refresh: () => void
}

// useStagedPaths (ADR-111 D2) — fetches GET /api/fleet/pending-paths ONCE per
// mount, the same poll-once-on-mount pattern Workflows.tsx's
// fetchWorkflowsPending uses for its aggregate banner. This is the per-file
// read that lets each Fleet Editor facet (Skills, Workflows, Agents rail,
// Resources tree) badge its OWN entries as "staged — not yet live" instead of
// only showing a page-level count. Best-effort: a failed fetch just leaves
// every badge unset (no false "staged" flicker, no page-breaking error) —
// mirrors fetchWorkflowsPending's own .catch(() => {}).
export function useStagedPaths(): StagedPaths {
  const [status, setStatus] = useState<FleetPendingPathsStatus | null>(null)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    let live = true
    api.getFleetPendingPaths()
      .then(s => { if (live) setStatus(s) })
      .catch(() => { /* leave badges unset on a transient failure */ })
    return () => { live = false }
  }, [nonce])

  const paths = useMemo(() => new Set(status?.paths ?? []), [status])
  const refresh = useCallback(() => setNonce(n => n + 1), [])
  return { status, paths, refresh }
}

/** Exact-path membership — for callers with a real repo-relative path (the
 * Resources tree, which already carries one per entry from GET /api/fleet/tree). */
export function isPathStaged(paths: Set<string>, path: string | null | undefined): boolean {
  return !!path && paths.has(path)
}

/** Prefix membership — for callers that only know a directory an artifact
 * lives under (a skill's `agents/<agent>/skills/<slug>/`, an agent's whole
 * `agents/<agent>/`), not one exact file path, since the staged set can
 * contain any file inside it. */
export function isPrefixStaged(paths: Set<string>, prefix: string): boolean {
  for (const p of paths) {
    if (p.startsWith(prefix)) return true
  }
  return false
}
